import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardBody, Col, Row, Media } from 'reactstrap';
import DataTable from '../../../Common/DataTable';
import { PlusCircle, Edit, ArrowRight, X } from 'react-feather';
import InterlocutoryRulingDataModal from './InterlocutoryRulingDataModal';
import ExpenseDataModal from '../../FinancialOperations/Expenses/ExpenseDataModal';
import FileCloseModal from './FileCloseModal';
import { fetchTableData, handleStateTransition } from '../../../../Api/ApiMethods';
import FilesCollapsible from '../../../Common/FilesCollapsible';

const InterlocutoryRulingInformations = ({ arbitrationId, arbitrationData, refreshArbitrationData }) => {
    const [, setSelectedRows] = useState([]);
    const [clearSelectedRows] = useState(false);
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add');
    const [selectedInterlocutoryRuling, setSelectedInterlocutoryRuling] = useState(null);

    // Masraf modal state'leri
    const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
    const [currentInterlocutoryRuling, setCurrentInterlocutoryRuling] = useState(null);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [expenseModalMode, setExpenseModalMode] = useState('add');
    const [isExpenseReadOnly, setIsExpenseReadOnly] = useState(false);

    // Dosya kapatma modal state'leri
    const [isFileCloseModalOpen, setIsFileCloseModalOpen] = useState(false);

    const interlocutoryRulingApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/InterlocutoryRulingInformation';

    const logSetData = (newData) => {
        setData(newData);
    };

    const handleRowSelected = useCallback(state => {
        setSelectedRows(state.selectedRows);
    }, []);

    useEffect(() => {
        if (arbitrationId) {
            // Modify the API endpoint to include arbitrationId as a query parameter
            const modifiedApiEndpoint = `${interlocutoryRulingApiUrl}?arbitrationId=${arbitrationId}`;
            
            fetchTableData({
                apiEndpoint: modifiedApiEndpoint,
                page,
                pageSize,
                search: '', // No search functionality
                setDataCallback: logSetData,
                setTotalRowsCallback: setTotalRows,
            });
        }
    }, [page, pageSize, arbitrationId, interlocutoryRulingApiUrl]);

    const toggleModal = () => {
        setModalOpen(!modalOpen);
        if (modalOpen) {
            setTimeout(() => setSelectedInterlocutoryRuling(null), 300);
        }
    };

    const openAddModal = () => {
        setModalMode('add');
        setSelectedInterlocutoryRuling(null);
        setModalOpen(true);
    };

    const openModifyModal = (interlocutoryRuling) => {
        setModalMode('modify');
        setSelectedInterlocutoryRuling(interlocutoryRuling);
        setModalOpen(true);
    };

    const refreshInterlocutoryRulingData = () => {
        if (arbitrationId) {
            const modifiedApiEndpoint = `${interlocutoryRulingApiUrl}?arbitrationId=${arbitrationId}`;

            fetchTableData({
                apiEndpoint: modifiedApiEndpoint,
                page,
                pageSize,
                search: '',
                setDataCallback: logSetData,
                setTotalRowsCallback: setTotalRows,
            });
        }
        refreshArbitrationData();
    };

    // Masraf modal handler fonksiyonları
    const handleAddExpenseModal = (interlocutoryRuling) => {
        setCurrentInterlocutoryRuling(interlocutoryRuling);
        setSelectedExpense(null);
        setExpenseModalMode('add');
        setIsExpenseReadOnly(false);
        setIsExpenseModalOpen(true);
    };

    const handleEditExpenseModal = (interlocutoryRuling, expense) => {
        const currentState = arbitrationData?.arbitrationState?.title;
        const isBilirkisiOdemesiBekleniyor = currentState === 'Bilirkişi Ödemesi Bekleniyor';
        
        setCurrentInterlocutoryRuling(interlocutoryRuling);
        setSelectedExpense(expense);
        setExpenseModalMode('modify');
        setIsExpenseReadOnly(!isBilirkisiOdemesiBekleniyor); // Sadece "Bilirkişi Ödemesi Bekleniyor" durumunda düzenlenebilir
        setIsExpenseModalOpen(true);
    };

    const closeExpenseModal = () => {
        setIsExpenseModalOpen(false);
        setCurrentInterlocutoryRuling(null);
        setSelectedExpense(null);
        setIsExpenseReadOnly(false);
    };

    const refreshExpenseData = () => {
        // Masraf eklendikten/güncellendiksinde tabloyu yenile
        refreshInterlocutoryRulingData();
    };

    // State transition function
    const handleArbitrationStateTransition = async () => {
        try {
            await handleStateTransition({ arbitrationId });
            refreshArbitrationData(); // Ana bileşendeki arbitration verisini yenile
        } catch (error) {
            // Toast mesajı handleStateTransition fonksiyonunda gösterildiği için burada tekrar göstermiyoruz
        }
    };

    // Dummy function for file closure (no functionality)
    const handleFileClose = () => {
        setIsFileCloseModalOpen(true);
    };

    // Function to get button text based on current state
    const getStateTransitionButtonText = () => {
        const currentState = arbitrationData?.arbitrationState?.title;
        
        switch (currentState) {
            case 'Ara Karar Bekleniyor':
                return 'Bilirkişi Ödemesine Gönder';
            case 'Bilirkişi Ödemesi Bekleniyor':
                return 'Bilirkişi Raporuna Gönder';
            default:
                return '';
        }
    };

    // Function to check if state transition button should be shown
    const shouldShowStateTransitionButton = () => {
        const currentState = arbitrationData?.arbitrationState?.title;
        return ['Ara Karar Bekleniyor', 'Bilirkişi Ödemesi Bekleniyor'].includes(currentState);
    };

    // Function to check if file close button should be shown
    const shouldShowFileCloseButton = () => {
        const currentState = arbitrationData?.arbitrationState?.title;
        // Tekrar Başvuru Bekleniyor ve Kapama Bekleniyor durumlarında gösterme
        return !['Tekrar Başvuru Bekleniyor', 'Kapama Bekleniyor'].includes(currentState);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('tr-TR');
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '';
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
    };

    const renderComment = (comments) => {
        // API'den gelen comments array'inden ilk comment'i göster
        if (!comments || !Array.isArray(comments) || comments.length === 0) return '-';
        const firstComment = comments[0]?.comment || '';
        if (!firstComment) return '-';
        
        return (
            <div
                title={firstComment}
                style={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '400px',
                }}
            >
                {firstComment}
            </div>
        );
    };

    const columns = [
        { 
            name: 'Geliş Tarihi', 
            selector: row => formatDate(row.arrivalDate), 
            sortable: true, 
            grow: 1,
            minWidth: '120px'
        },
        { 
            name: 'Son Gün', 
            selector: row => formatDate(row.lastDay), 
            sortable: true, 
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Birinci Bilirkişi',
            selector: row => row.primaryExpertWitness?.fullName || '',
            sortable: true,
            cell: row => {
                const value = row.primaryExpertWitness?.fullName || '';
                return (
                    <div
                        title={value}
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '150px',
                        }}
                    >
                        {value}
                    </div>
                );
            },
            grow: 1,
            minWidth: '150px'
        },
        {
            name: 'İkinci Bilirkişi',
            selector: row => row.secondaryExpertWitness?.fullName || '',
            sortable: true,
            cell: row => {
                const value = row.secondaryExpertWitness?.fullName || '';
                return (
                    <div
                        title={value}
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            maxWidth: '150px',
                        }}
                    >
                        {value}
                    </div>
                );
            },
            grow: 1,
            minWidth: '150px'
        },
        { 
            name: 'Bilirkişi Ücreti', 
            selector: row => formatCurrency(row.expertWitnessPrice), 
            sortable: true, 
            grow: 1,
            minWidth: '140px'
        },
        {
            name: 'Yatırıldı mı?',
            selector: row => row.isDeposited ? 'Evet' : 'Hayır',
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Yorum',
            selector: row => {
                // API'den gelen comments array'inden ilk comment'i göster
                if (!row.comments || !Array.isArray(row.comments) || row.comments.length === 0) return '-';
                return row.comments[0]?.comment || '-';
            },
            cell: row => renderComment(row.comments),
            grow: 3,
            minWidth: '100px'
        },
        {
            name: 'Masraf',
            cell: (row) => {
                const expense = row.expenses; // Ara karar objesinde expenses alanı
                const currentState = arbitrationData?.arbitrationState?.title;
                const isBilirkisiOdemesiBekleniyor = currentState === 'Bilirkişi Ödemesi Bekleniyor';
                
                if (!expense) {
                    // Masraf yoksa sadece "Bilirkişi Ödemesi Bekleniyor" durumunda "Ekle" butonu göster
                    if (isBilirkisiOdemesiBekleniyor) {
                        return (
                            <div
                                className="btn btn-outline-success d-flex align-items-center justify-content-center"
                                onClick={() => handleAddExpenseModal(row)}
                                title="Masraf Ekle"
                                style={{ minWidth: '80px' }}
                            >
                                Ekle
                            </div>
                        );
                    } else {
                        // Diğer durumlarda masraf yoksa boş göster
                        return <div style={{ minWidth: '80px', textAlign: 'center' }}>-</div>;
                    }
                } else {
                    // Masraf varsa her durumda görüntüle (düzenleme yetkisi duruma göre)
                    const isReadOnly = !isBilirkisiOdemesiBekleniyor;
                    return (
                        <div
                            className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                            onClick={() => handleEditExpenseModal(row, expense)}
                            title={isReadOnly ? `${expense.expenseType || 'Masraf'} - Görüntülemek için tıklayın` : `${expense.expenseType || 'Masraf'} - Düzenlemek için tıklayın`}
                            style={{ cursor: 'pointer', minWidth: '80px' }}
                        >
                            <small className="text-primary fw-bold">
                                {expense.amount ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(expense.amount) : '-'}
                            </small>
                        </div>
                    );
                }
            },
            ignoreRowClick: true,
            button: true,
            grow: 1.2,
            minWidth: '120px'
        },
        {
            name: 'Düzenle',
            cell: (row) => (
                <div
                    className="btn btn-outline-warning d-flex align-items-center justify-content-center"
                    onClick={() => openModifyModal(row)}
                    title="Düzenle"
                >
                    <Edit size={18} />
                </div>
            ),
            ignoreRowClick: true,
            right: true,
            button: true,
            grow: 1.2,
            minWidth: '100px'
        },
    ];

    return (
        <>
            <Row className="mt-4">
                <Col sm='12'>
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-end align-items-center w-100 file-content flex-wrap">
                                <Media body className='text-end mt-3'>
                                    <div className="btn btn-outline-primary ms-2" onClick={openAddModal} style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}>
                                        <PlusCircle size={18} className="me-1" />
                                        {' Ara Karar Ekle'}
                                    </div>
                                    {shouldShowStateTransitionButton() && (
                                        <div 
                                            className="btn btn-outline-success ms-2"
                                            onClick={handleArbitrationStateTransition} 
                                            style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}
                                        >
                                            <ArrowRight size={18} className="me-1" />
                                            {getStateTransitionButtonText()}
                                        </div>
                                    )}
                                    {shouldShowFileCloseButton() && (
                                        <div 
                                            className="btn btn-outline-secondary ms-2" 
                                            onClick={handleFileClose} 
                                            style={{ height: '35px', display: 'inline-flex', alignItems: 'center' }}
                                        >
                                            <X size={18} className="me-1" />
                                            Dosya Kapamaya Gönder
                                        </div>
                                    )}
                                </Media>
                            </div>

                            <div style={{ height: "40px" }}></div>

                            <div className='table-responsive'>
                                <DataTable
                                    columns={columns}
                                    data={data}
                                    onSelectedRowsChange={handleRowSelected}
                                    pagination
                                    paginationServer
                                    clearSelectedRows={clearSelectedRows}
                                    paginationTotalRows={totalRows}
                                    onChangePage={page => setPage(page)}
                                    onChangeRowsPerPage={pageSize => { setPage(1); setPageSize(pageSize); }}
                                    striped
                                    noDataComponent={<div className="p-4 text-center">Ara karar bulunamadı.</div>}
                                    progressPending={!data}
                                    progressComponent={<p>Yükleniyor...</p>}
                                />
                            </div>

                            {Array.isArray(data) && data.length > 0 && (
                                <div className="mt-4">
                                    <h6 className="mb-3">Dosyalar</h6>
                                    {data.map((row, idx) => {
                                        const arrival = row.arrivalDate ? new Date(row.arrivalDate).toLocaleDateString('tr-TR') : null;
                                        return (
                                            <FilesCollapsible
                                                key={row.id}
                                                title={arrival ? `Ara Karar: ${arrival}` : `Ara Karar #${idx + 1} Dosyaları`}
                                                referenceId={row.id}
                                            />
                                        );
                                    })}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            
            <InterlocutoryRulingDataModal
                apiEndpoint={interlocutoryRulingApiUrl}
                isOpen={modalOpen}
                toggle={toggleModal}
                mode={modalMode}
                selectedInterlocutoryRuling={selectedInterlocutoryRuling}
                refreshData={refreshInterlocutoryRulingData}
                arbitrationId={arbitrationId}
            />

            {/* Masraf Ekleme/Düzenleme Modalı */}
            <ExpenseDataModal
                apiEndpoint={process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/expense'}
                isOpen={isExpenseModalOpen}
                toggle={closeExpenseModal}
                mode={expenseModalMode}
                selectedExpense={selectedExpense}
                refreshData={refreshExpenseData}
                customerId={null}
                autoAssignCustomer={false}
                accidentFile={[]}
                callformId={null}
                relatedArbitrationSchema={currentInterlocutoryRuling ? [String(currentInterlocutoryRuling.id)] : []}
                arbitrationSchemaType="BILIRKISI_ODEMESI"
                readOnlyView={isExpenseReadOnly}
            />

            {/* Dosya Kapatma Modalı */}
            <FileCloseModal
                isOpen={isFileCloseModalOpen}
                toggle={() => setIsFileCloseModalOpen(false)}
                arbitrationId={arbitrationId}
                refreshArbitrationData={refreshArbitrationData}
            />
        </>
    );
};

export default InterlocutoryRulingInformations;
